import { createContext } from "react";

export type LoadStatusContextValue = {
  /**
   * If the currently loaded application is considered stale or not. If the application
   * is considered stale, any in-app navigation will result in a hard re-load instead of
   * using the routing library.
   */
  isStale: boolean;
  /**
   * Callback to force the application to be considered stale.
   * Useful for some exceptional (and critical) cases with where we
   * want to get the user to reload the app when convienient.
   */
  forceIsStale: () => void;
  /**
   * Callback to trigger if the application requires a manual reload or not. If the application
   * requires a manual reload (via this callback, or if the app gets exceedingly stale) the user
   * will not be able to do anything and an overlay will be displayed which asks the user to refresh
   * the page.
   */
  onRequiresManualReload: () => void;
  /** If a manual reload is required or not. */
  requiresManualReload: boolean;
  /** If the reload required overlay should be prevented or not. This is useful for cases where we
   * want to prevent the overlay from being shown because something else will force a reload.
   */
  preventReloadRequiredOverlay: boolean;
  /** Callback to prevent the reload required overlay from being shown. */
  onPreventReloadRequiredOverlay: () => void;
  /** If the application should be automatically reloaded or not. */
  forceUnauthenticatedReload: boolean;
  /** Callback to force an automatic reload. */
  onForceUnauthenticatedReload: () => void;
  /** If the application needs to be reloaded or not. */
  needsReload: boolean;
};

/**
 * Interface to track the current application loading status
 *
 * @see {@link load/hooks!useLoadStatus} for how to consume in general application code.
 */
const LoadStatusContext = createContext<LoadStatusContextValue>({
  isStale: false,
  requiresManualReload: false,
  onRequiresManualReload: () => console.warn("onRequiresManualRefresh is not bound"),
  forceIsStale: () => console.warn("forceIsStale is not bound"),
  preventReloadRequiredOverlay: false,
  onPreventReloadRequiredOverlay: () => console.warn("onPreventReloadRequiredOverlay is not bound"),
  forceUnauthenticatedReload: false,
  onForceUnauthenticatedReload: () => console.warn("onForceUnauthenticatedReload is not bound"),
  needsReload: false,
});

export default LoadStatusContext;
